import React, { useState } from 'react';
import { Button, Section } from '../../../common';
import { ButtonGroup, Toolbar, ToolbarItem } from '@emorg-prd/standard-react';
import { PencilIcon, TrashcanIcon } from 'react-unity';
import AccountConnectivitySettingsModal from './AccountConnectivitySettingsModal';
import AWSAccountConnectivityConfiguration from '../../../../models/entities/AWSAccountConnectivityConfiguration';
import AWSAccountRequest from '../../../../models/entities/AWSAccountRequest';
import Table from '../../../common/components/Table';
import './AccountConnectivitySettingsTable.css';
import Paginator from '../../../common/tables/Paginator';

interface AccountConnectivitySettingsTableProps {
	network: AWSAccountConnectivityConfiguration[];
	readonly?: boolean;
	onChange: any;
	account: AWSAccountRequest;
}

const AccountConnectivitySettingsTable = ({
	network,
	readonly,
	onChange,
	account,
}: AccountConnectivitySettingsTableProps) => {
	const [connectivitySettingsModal, setConnectivitySettingsModal] = useState<boolean>(false);
	const [editingConfiguration, setEditingConfiguration] = useState<AWSAccountConnectivityConfiguration>(null);

	const handleClick = () => {
		setEditingConfiguration(null);
		setConnectivitySettingsModal(true);
	};

	const sortByPriorityAsc = (a: AWSAccountConnectivityConfiguration, b: AWSAccountConnectivityConfiguration) =>
		a.priority - b.priority;

	const handleConfigurationChange = (networkConfig: AWSAccountConnectivityConfiguration) => {
		const current = Array.isArray(network) ? network : [];
		let updated: AWSAccountConnectivityConfiguration[];

		if (editingConfiguration != null) {
			const idx = current.findIndex((n) => n.id === editingConfiguration.id);
			updated = [...current];
			updated[idx] = networkConfig;
		} else {
			updated = [...current, networkConfig];
		}

		//priority could be the order in this table
		// const updatedSorted = [...updated].sort(sortByPriorityAsc);

		onChange(updated);
		setConnectivitySettingsModal(false);
		setEditingConfiguration(null);
	};

	const handleEditingConfiguration = (configuration: AWSAccountConnectivityConfiguration) => {
		setEditingConfiguration(configuration);
		setConnectivitySettingsModal(true);
	};

	const handleDeleteOrRemoveRule = (configuration: AWSAccountConnectivityConfiguration) => {
		const net = network.filter((netConfig) => netConfig !== configuration);
		onChange(net);
	};

	const handleSeeDetails = (configuration: AWSAccountConnectivityConfiguration) => {
		setEditingConfiguration(configuration);
		setConnectivitySettingsModal(true);
	};

	return (
		<>
			<Section title="Connectivity Settings">
				<Toolbar>
					<ToolbarItem>
						<Button
							variant="primary"
							size="small"
							disabled={readonly || ((account?.regionNames?.length ?? 0) > 0 && (network?.length ?? 0) >= account.regionNames.length)} //one vpc per region
							onClick={handleClick}
							name="Add Configuration"
						/>
					</ToolbarItem>
				</Toolbar>
				<Table
					caption="Network configuration"
					emptyMessage="No network configuration found"
					optionalClass="connectivity-settings-table-style"
					footer={
						<Paginator
							data={network}
							onPageChange={(page) => {
								network = page;
							}}
						/>
					}
				>
					<Table.Head>
						<Table.Head.Row>
							<Table.Head.Cell> # </Table.Head.Cell>
							<Table.Head.Cell> Region </Table.Head.Cell>
							<Table.Head.Cell> Priority </Table.Head.Cell>
							<Table.Head.Cell> Size </Table.Head.Cell>
							<Table.Head.Cell> Enable S3 Gateway Endpoint </Table.Head.Cell>
							<Table.Head.Cell> CIDR </Table.Head.Cell>
							<Table.Head.Cell> Route 53 Resolver Rule Association </Table.Head.Cell>
							<Table.Head.Cell>{/* actions column */}</Table.Head.Cell>
						</Table.Head.Row>
					</Table.Head>

					<Table.Body>
						{(network?.length ?? 0) > 0
							? network.map((net, rowIndex) => (
									<Table.Row key={rowIndex}>
										<Table.Cell>{rowIndex + 1}</Table.Cell>
										<Table.Cell>{net.region}</Table.Cell>
										<Table.Cell>{net.priority}</Table.Cell>
										<Table.Cell>{account?.vpcSizeId}</Table.Cell>
										<Table.Cell>{net.enableS3GatewayEndpoint.toString()}</Table.Cell>
										<Table.Cell>{net.cidr}</Table.Cell>
										<Table.Cell>{net.route53ResolverRuleAssociations}</Table.Cell>
										<Table.Cell>
											{!readonly ? (
												<ButtonGroup>
													<Button
														variant="primary"
														size="small"
														onClick={() => handleEditingConfiguration(net)}
													>
														<PencilIcon size="small" />
													</Button>
													<Button
														variant="primary"
														size="small"
														onClick={() => handleDeleteOrRemoveRule(net)}
													>
														<TrashcanIcon size="small" />
													</Button>
												</ButtonGroup>
											) : (
												<Button
													style={{ zIndex: 0 }}
													variant="primary"
													size="small"
													onClick={() => handleSeeDetails(net)}
												>
													See details
												</Button>
											)}
										</Table.Cell>
									</Table.Row>
								))
							: null}
					</Table.Body>
				</Table>
			</Section>
			{connectivitySettingsModal && (
				<AccountConnectivitySettingsModal
					isReadOnly={readonly}
					visible={connectivitySettingsModal}
					setConnectivitySettingsModal={setConnectivitySettingsModal}
					onConfirm={handleConfigurationChange}
					editingConfiguration={editingConfiguration}
					account={account}
					network={network}
				/>
			)}
		</>
	);
};

export default AccountConnectivitySettingsTable;
